import React, { Fragment, useState } from 'react'
import { useForm, FormContext, Controller } from 'react-hook-form'
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
} from 'react-feather'
import { isEmpty, omitBy, isNull } from 'lodash'
import clsx from 'clsx'
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Switch,
  Tooltip,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import helpers from 'helpers'

import useStyles from './styles'
import { DaiControlHeader, SolutionController } from './components'

import useSnackbar from 'hooks/useSnackbar'
import useDaiOptOut from 'hooks/useDaiOptOut'

import theme from 'theme'
import schema from './schema'
import * as service from 'service'
import constants from 'constants/index'

const DaiControlSection = () => {
  const [loading, setLoading] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState({})

  const classes = useStyles()
  const snackbar = useSnackbar()
  const {
    daiOptOuts,
    isLoadingStart: isDaiOptOutLoading,
    refresh,
  } = useDaiOptOut()

  const { GROUP_MANAGEABLE, OWNED_BY, SOLUTION_MANAGEABLE, STATUSES } =
    constants.dai.solutions

  const groups = daiOptOuts?.groups ?? []
  const solutions = daiOptOuts?.solutions ?? []

  const solutionControls = [
    ...groups
      .filter((group) => group.manageable === GROUP_MANAGEABLE.PER_SOLUTION)
      .flatMap((group) =>
        (group.solutions ?? []).filter(
          (solution) => solution.manageable === SOLUTION_MANAGEABLE.INDIVIDUAL,
        ),
      ),
    ...solutions,
  ]

  const defaultValues = {
    global: daiOptOuts?.global?.status,
    ...Object.fromEntries(groups.map((group) => [group.tag, group.status])),
    ...Object.fromEntries(
      solutionControls.map((solution) => [solution.tag, solution.status]),
    ),
  }

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues,
  })

  const { handleSubmit, watch, control, errors } = formMethods

  const allValues = watch()

  const isGlobalEnabled = allValues.global !== STATUSES.DISABLED
  const hasItems = !isEmpty(groups) || !isEmpty(solutions)

  const handleStatusChange = (checked) =>
    checked ? STATUSES.ENABLED : STATUSES.DISABLED

  const pickByTag = (formData, items = []) => {
    return omitBy(
      Object.fromEntries(items.map(({ tag }) => [tag, formData[tag]])),
      isNull,
    )
  }

  const toggleCollapse = (group) => {
    return setCollapsedGroups((prev) => ({
      ...prev,
      [group.tag]: !prev[group.tag],
    }))
  }

  const onSubmit = async (formData) => {
    try {
      setLoading(true)

      await service.dponet.daiOptOuts.manage({
        daiOptOut: {
          ...(!isNull(formData.global) && { global: formData.global }),
          groups: pickByTag(formData, groups),
          solutions: pickByTag(formData, solutionControls),
        },
      })

      snackbar.open({
        message: 'Configurações de IA atualizadas com sucesso!',
        variant: 'success',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormContext {...formMethods}>
      <Box
        component="form"
        id="dai-control-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Paper variant="outlined">
          <DaiControlHeader />
          <Collapse in={(isGlobalEnabled && hasItems) || isDaiOptOutLoading}>
            {isDaiOptOutLoading ? (
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(2)}
                p={2}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    height={24}
                    width="100%"
                    variant="rect"
                  />
                ))}
              </Box>
            ) : (
              <Fragment>
                <Divider />
                <Box p={2}>
                  {groups.map((group, index) => {
                    const fieldName = group.tag
                    const isLockedByCollaborator =
                      group.ownedBy === OWNED_BY.COLLABORATOR
                    const isLockedByOrganization =
                      group.ownedBy === OWNED_BY.ORGANIZATION
                    const isGroupLocked =
                      (isLockedByCollaborator || isLockedByOrganization) &&
                      group.status === STATUSES.DISABLED
                    const isGroupEnabled =
                      allValues[fieldName] !== STATUSES.DISABLED
                    const isPerSolution =
                      group.manageable === GROUP_MANAGEABLE.PER_SOLUTION
                    const isExpanded = !collapsedGroups[group.tag]

                    return (
                      <Box key={index}>
                        <Controller
                          as={
                            <FormControl fullWidth>
                              <FormControlLabel
                                label={
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gridGap={theme.spacing(1)}
                                  >
                                    {group.name}
                                    {isPerSolution && isGroupEnabled && (
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleCollapse(group)}
                                      >
                                        {isExpanded ? (
                                          <ChevronUpIcon size={18} />
                                        ) : (
                                          <ChevronDownIcon size={18} />
                                        )}
                                      </IconButton>
                                    )}
                                  </Box>
                                }
                                labelPlacement="start"
                                classes={{
                                  root: classes.formControlLabel,
                                }}
                                control={
                                  <Tooltip
                                    title={
                                      isGroupLocked
                                        ? 'Esta funcionalidade não está disponível. Entre em contato com o administrador para mais informações.'
                                        : ''
                                    }
                                  >
                                    <Box
                                      className={clsx({
                                        [classes.locked]: isGroupLocked,
                                      })}
                                    >
                                      <Switch
                                        checked={isGroupEnabled}
                                        color="primary"
                                        disabled={isGroupLocked}
                                      />
                                    </Box>
                                  </Tooltip>
                                }
                              />
                              {!!errors?.[fieldName] && (
                                <FormHelperText error>
                                  {errors?.[fieldName]?.message}
                                </FormHelperText>
                              )}
                            </FormControl>
                          }
                          control={control}
                          name={fieldName}
                          onChange={([event]) =>
                            handleStatusChange(event.target.checked)
                          }
                        />
                        {isPerSolution && (
                          <Collapse
                            in={
                              isGroupEnabled &&
                              isExpanded &&
                              !isEmpty(group.solutions)
                            }
                          >
                            <Box pl={3}>
                              {group.solutions?.map(
                                (solution, solutionIndex) => (
                                  <SolutionController
                                    key={solutionIndex}
                                    solution={solution}
                                    groupFieldName={fieldName}
                                    allValues={allValues}
                                    errors={errors}
                                    control={control}
                                  />
                                ),
                              )}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    )
                  })}
                  {solutions.map((solution, solutionIndex) => (
                    <SolutionController
                      key={solutionIndex}
                      solution={solution}
                      allValues={allValues}
                      errors={errors}
                      control={control}
                    />
                  ))}
                </Box>
              </Fragment>
            )}
          </Collapse>
          <Divider />
          <Box display="flex" justifyContent="flex-end" p={2}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form="dai-control-form"
              disabled={loading}
            >
              Salvar configurações de IA
            </Button>
          </Box>
        </Paper>
      </Box>
    </FormContext>
  )
}

export default DaiControlSection
